import React, { useRef, useState } from "react";
import { Animated, Modal, Pressable, ScrollView, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../../theme";
import mobileStyles from "../MyTicketsScreen.mobile.style";
import { FilterOptions } from "../hooks/useMyTicketsListing";

export type MobileFilters = {
  status: string;
  urgency: string;
  category: string;
  assignee: string;
  location: string;
  dateRange: string;
};

const EMPTY_FILTERS: MobileFilters = {
  status: "",
  urgency: "",
  category: "",
  assignee: "",
  location: "",
  dateRange: "",
};

/** Internal status key → display label */
const STATUS_KEY_TO_LABEL: Record<string, string> = {
  new: "New",
  progress: "In Progress",
  "waiting-it": "Escalated to IT",
  "waiting-tmv": "Escalated to TMV",
  "submitted-ado": "Submitted to ADO",
  "waiting-user": "Waiting on User",
  resolved: "Closed",
};

/** Internal urgency key → display label */
const URGENCY_KEY_TO_LABEL: Record<string, string> = {
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

// Fallback static option lists used when no ticket data is loaded yet
const FALLBACK_STATUS_OPTIONS = [
  "All", "New", "In Progress", "Escalated to IT", "Escalated to TMV",
  "Submitted to ADO", "Waiting on User", "Closed",
];
const FALLBACK_URGENCY_OPTIONS = ["All", "Critical", "High", "Medium", "Low"];
const FALLBACK_CATEGORY_OPTIONS = [
  "All", "System Issue", "Data Issue", "Access Request", "Equipment Issue",
  "Process / How-To", "Reporting", "Enhancement Request", "System Enhancement",
];
const FALLBACK_LOCATION_OPTIONS = ["All Locations"];

const DATE_RANGE_OPTIONS = [
  "Any time",
  "Last 7 days",
  "Last 30 days",
  "Last 90 days",
  "This year",
];

type FilterDropdownProps = {
  label: string;
  value: string;
  options: string[];
  openKey: string;
  openDropdown: string | null;
  setOpenDropdown: (k: string | null) => void;
  onSelect: (v: string) => void;
  emptyLabel?: string;
  isDark?: boolean;
  colors?: any;
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  value,
  options,
  openKey,
  openDropdown,
  setOpenDropdown,
  onSelect,
  emptyLabel,
  isDark,
  colors,
}) => {
  const isOpen = openDropdown === openKey;
  const displayValue = value || emptyLabel || "All";

  return (
    <View style={mobileStyles.filterSection}>
      <Text style={[mobileStyles.filterSectionLabel, isDark && { color: colors.text.secondary }]}>{label}</Text>
      <Pressable
        style={[mobileStyles.filterDropdownWrap, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}
        onPress={() => setOpenDropdown(isOpen ? null : openKey)}
      >
        <Text style={[mobileStyles.filterDropdownText, isDark && { color: colors.text.primary }]}>{displayValue}</Text>
        <MaterialIcons
          name={isOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
          size={22}
          color={isDark ? colors.text.secondary : "#3a3a3a"}
        />
      </Pressable>
      {isOpen && (
        <View style={[mobileStyles.filterDropdownList, isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
          {options.map((opt, idx) => {
            const isSelected =
              value === opt ||
              (!value && (opt === "All" || opt === "All Locations" || opt === "Any time"));
            return (
              <Pressable
                key={opt}
                style={({ pressed }) => [
                  mobileStyles.filterDropdownItem,
                  isDark && { backgroundColor: colors.background.elevated, borderBottomColor: colors.border.divider },
                  isSelected ? (isDark ? { backgroundColor: "rgba(61,139,255,0.15)" } : mobileStyles.filterDropdownItemSelected) : null,
                  idx === options.length - 1 ? { borderBottomWidth: 0 } : null,
                  pressed ? (isDark ? { backgroundColor: "rgba(61,139,255,0.08)" } : mobileStyles.filterDropdownItemPressed) : null,
                ]}
                onPress={() => {
                  onSelect(opt === "All" || opt === "All Locations" || opt === "Any time" ? "" : opt);
                  setOpenDropdown(null);
                }}
              >
                {isSelected ? (
                  <MaterialIcons name="check" size={16} color={isDark ? colors.brand.primary : "#034895"} style={{ marginRight: 8 }} />
                ) : (
                  <View style={{ width: 24 }} />
                )}
                <Text
                  style={[
                    mobileStyles.filterDropdownItemText,
                    isDark && { color: colors.text.primary },
                    isSelected ? (isDark ? { color: colors.brand.primary, fontWeight: "700" } : mobileStyles.filterDropdownItemTextSelected) : null,
                  ]}
                >
                  {opt}
                </Text>
              </Pressable>
            );
          })}
        </View>
      )}
    </View>
  );
};

type Props = {
  visible: boolean;
  filters: MobileFilters;
  /** Dynamic filter options derived from actual ticket data. Falls back to static lists when absent. */
  filterOptions?: FilterOptions;
  onApply: (filters: MobileFilters) => void;
  onClose: () => void;
  onSnackbar?: (msg: string, isSuccess?: boolean) => void;
};

const MyTicketsFilterSheet: React.FC<Props> = ({ visible, filters, filterOptions, onApply, onClose, onSnackbar }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [draft, setDraft] = useState<MobileFilters>(filters);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Build dynamic option lists from ticket data; fall back to static lists when data is loading
  const statusOptions = filterOptions?.statuses?.length
    ? ["All", ...filterOptions.statuses.map((k) => STATUS_KEY_TO_LABEL[k] ?? k)]
    : FALLBACK_STATUS_OPTIONS;
  const urgencyOptions = filterOptions?.urgencies?.length
    ? ["All", ...filterOptions.urgencies.map((k) => URGENCY_KEY_TO_LABEL[k] ?? k)]
    : FALLBACK_URGENCY_OPTIONS;
  const categoryOptions = filterOptions?.categories?.length
    ? ["All", ...filterOptions.categories]
    : FALLBACK_CATEGORY_OPTIONS;
  const locationOptions = filterOptions?.locations?.length
    ? ["All Locations", ...filterOptions.locations]
    : FALLBACK_LOCATION_OPTIONS;
  const clearSnackAnim = useRef(new Animated.Value(-80)).current;
  const [showClearSnack, setShowClearSnack] = useState(false);

  const handleApply = () => {
    const count = [draft.status, draft.urgency, draft.category, draft.assignee, draft.location, draft.dateRange].filter(Boolean).length;
    onApply(draft);
    if (count > 0) {
      onSnackbar?.(`${count} filter${count !== 1 ? "s" : ""} applied`, true);
    } else {
      onSnackbar?.("Showing all tickets", false);
    }
    onClose();
  };

  const handleClearAll = () => {
    setDraft(EMPTY_FILTERS);
    setOpenDropdown(null);
    setShowClearSnack(true);
    clearSnackAnim.setValue(-80);
    Animated.sequence([
      Animated.timing(clearSnackAnim, { toValue: 0, duration: 300, useNativeDriver: true }),
      Animated.delay(2000),
      Animated.timing(clearSnackAnim, { toValue: -80, duration: 250, useNativeDriver: true }),
    ]).start(() => setShowClearSnack(false));
  };

  React.useEffect(() => {
    if (visible) {
      setDraft(filters);
      setOpenDropdown(null);
    }
  }, [visible, filters]);

  const activeCount = [
    draft.status,
    draft.urgency,
    draft.category,
    draft.assignee,
    draft.location,
    draft.dateRange,
  ].filter(Boolean).length;

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      {showClearSnack && (
        <Animated.View
          style={{
            position: "absolute",
            top: 14,
            left: 12,
            right: 12,
            zIndex: 999,
            borderRadius: 10,
            backgroundColor: isDark ? colors.background.elevated : "#1a2332",
            flexDirection: "row",
            alignItems: "center",
            paddingHorizontal: 16,
            paddingVertical: 14,
            gap: 10,
            shadowColor: "#000",
            shadowOpacity: 0.2,
            shadowRadius: 8,
            shadowOffset: { width: 0, height: 4 },
            elevation: 8,
            transform: [{ translateY: clearSnackAnim }],
          }}
        >
          <MaterialIcons name="check" size={18} color="#ffffff" />
          <Text style={{ fontSize: 15, fontWeight: "700", color: "#ffffff" }}>Filters cleared</Text>
        </Animated.View>
      )}
      <Pressable style={mobileStyles.sheetOverlay} onPress={onClose}>
        <Pressable style={[mobileStyles.sheetContainer, isDark && { backgroundColor: colors.background.surface }]} onPress={() => {}}>
          {/* Dark header */}
          <View style={mobileStyles.sheetHeaderDark}>
            <Pressable onPress={onClose} style={mobileStyles.sheetCloseBtn}>
              <MaterialIcons name="close" size={20} color="#ffffff" />
            </Pressable>
            <Text style={mobileStyles.sheetTitleDark}>Filters</Text>
            <Pressable onPress={handleClearAll}>
              <Text style={mobileStyles.sheetClearAllDark}>Clear All</Text>
            </Pressable>
          </View>

          <ScrollView
            contentContainerStyle={mobileStyles.sheetScrollContent}
            showsVerticalScrollIndicator={false}
          >
            <FilterDropdown
              label="STATUS"
              value={draft.status}
              options={statusOptions}
              openKey="status"
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onSelect={(v) => setDraft((d) => ({ ...d, status: v }))}
              isDark={isDark}
              colors={colors}
            />
            <FilterDropdown
              label="URGENCY"
              value={draft.urgency}
              options={urgencyOptions}
              openKey="urgency"
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onSelect={(v) => setDraft((d) => ({ ...d, urgency: v }))}
              isDark={isDark}
              colors={colors}
            />
            <FilterDropdown
              label="CATEGORY"
              value={draft.category}
              options={categoryOptions}
              openKey="category"
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onSelect={(v) => setDraft((d) => ({ ...d, category: v }))}
              isDark={isDark}
              colors={colors}
            />
            <FilterDropdown
              label="LOCATION / MARKET / REGION"
              value={draft.location}
              options={locationOptions}
              openKey="location"
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onSelect={(v) => setDraft((d) => ({ ...d, location: v }))}
              emptyLabel="All Locations"
              isDark={isDark}
              colors={colors}
            />
            <FilterDropdown
              label="DATE RANGE"
              value={draft.dateRange}
              options={DATE_RANGE_OPTIONS}
              openKey="dateRange"
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              onSelect={(v) => setDraft((d) => ({ ...d, dateRange: v }))}
              emptyLabel="Any time"
              isDark={isDark}
              colors={colors}
            />
          </ScrollView>

          <View style={[mobileStyles.sheetFooter, isDark && { borderTopColor: colors.border.divider }]}>
            <Pressable onPress={handleClearAll} style={mobileStyles.sheetFooterClearAll}>
              <Text style={[mobileStyles.sheetFooterClearAllText, isDark && { color: colors.text.primary }]}>Clear All</Text>
            </Pressable>
            <Pressable
              style={({ pressed }) => [
                mobileStyles.applyBtn,
                pressed ? mobileStyles.applyBtnPressed : null,
              ]}
              onPress={handleApply}
            >
              <Text style={mobileStyles.applyBtnText}>
                Apply Filters{activeCount > 0 ? ` (${activeCount})` : ""}
              </Text>
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default MyTicketsFilterSheet;

